import { Box, Flex, useDisclosure, Button } from "@chakra-ui/react";
import SettingsDescription from "pages/Settings/elements/SettingsDescription";
import SettingsHeader from "pages/Settings/elements/SettingsHeader";
import { useCallback, useEffect, useState } from "react";
import { LoanTypeViewModel } from "./loan-type.view-model";
import { apiGetRequest } from "utils/api";
import { LoanTypeModal } from "./loan-type-modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Loader } from "components/elements/loader/loader";
import { LoanType } from "./loan-type";
import { useTenant } from "modules/tenant/providers/tenant-provider";

export function LoanTypes() {
  const [loanTypeForModal, setLoanTypeForModal] = useState<LoanTypeViewModel | null>(null);
  const [loanTypes, setLoanTypes] = useState<LoanTypeViewModel[]>([]);
  const { user } = useTenant();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = useState(true);

  const loadLoanTypes = useCallback(async () => {
    setIsLoading(true);

    try {
      const { loan_types } = await apiGetRequest<{
        loan_types: LoanTypeViewModel[];
      }>("/supabase/loan_types/select");

      // Filter loan types for the current company
      const companyLoanTypes = loan_types.filter(type => type.lender_company === user.company.id);

      setLoanTypes(sortLoanTypes(companyLoanTypes));
    } finally {
      setIsLoading(false);
    }
  }, [user.company.id]);

  useEffect(() => {
    loadLoanTypes();
  }, [loadLoanTypes]);

  // Loan Type Modal
  function handleEdit(loanType: LoanTypeViewModel) {
    setLoanTypeForModal(loanType);
    onOpen();
  }

  function handleNewLoanType() {
    const newLoanType = createNewLoanType(user.company.id);
    setLoanTypeForModal(newLoanType);
    onOpen();
  }

  function handleCloseLoanTypeModal() {
    setLoanTypeForModal(null);
    onClose();
  }

  function handleOnLoanTypeDeleted(loanType: LoanTypeViewModel) {
    setLoanTypes(state => state.filter(x => x.id !== loanType.id));
  }

  function handleOnLoanTypeUpdate(loanType: LoanTypeViewModel) {
    setLoanTypes(loanTypes => loanTypes.map(lt => (lt.id === loanType.id ? loanType : lt)));
  }

  function handleOnLoanTypeAdded(loanType: LoanTypeViewModel) {
    setLoanTypes(state => [...state, loanType]);
  }
  ///////////////////////////////////////

  return (
    <Flex direction="column" gap={4}>
      <Box>
        <SettingsHeader title="Loan Types" />
        <SettingsDescription text="Add additional fields to specific loan types" />
      </Box>

      {isLoading ? (
        <Loader>Loading Types...</Loader>
      ) : (
        <>
          <Box>
            <Button
              onClick={handleNewLoanType}
              leftIcon={<FontAwesomeIcon icon={["far", "plus"]} />}
            >
              New Loan Type
            </Button>
          </Box>
          <Box>
            {loanTypes.map(loanType => (
              <LoanType
                key={loanType.id}
                loanType={loanType}
                onEdit={handleEdit}
                onUpdate={handleOnLoanTypeUpdate}
                onDelete={handleOnLoanTypeDeleted}
              />
            ))}
          </Box>
        </>
      )}

      {loanTypeForModal && (
        <LoanTypeModal
          loanType={loanTypeForModal}
          isOpen={isOpen}
          onClose={handleCloseLoanTypeModal}
          onAdd={handleOnLoanTypeAdded}
          onUpdate={handleOnLoanTypeUpdate}
        />
      )}
    </Flex>
  );
}

function sortLoanTypes(loanTypes: LoanTypeViewModel[]) {
  if (!loanTypes) return [];

  return loanTypes
    .sort((a, b) => a.title.localeCompare(b.title))
    .map(loanType => {
      return {
        ...loanType,
        fields: (loanType.fields || []).sort((a, b) => a.order - b.order),
      };
    });
}

function createNewLoanType(companyId: string): LoanTypeViewModel {
  return {
    id: "",
    title: "",
    description: "",
    fields: [],
    lender_company: companyId,
  };
}
